import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { AppIcon, Badge, Button, ListTable, Notice, Plate, SelectField, TabPanel, Tabs, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { formatDate, formatMoney, t, type MessageKey } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { articlesQuery, entriesQuery, MONEY, moneyApi, rulesQuery, templatesQuery, today, type Article, type Entry, type Rule, type Template } from "./api.ts";

const route = getRouteApi("/app/money/opex");

export type OpexTab = "entries" | "rules" | "templates";

const ACTIVITIES = ["", "SER", "RND", "SRV"] as const;

const activityOptions = () => ACTIVITIES.map((a) => ({ value: a, label: a === "" ? t("opex.activity.none") : a }));

const ORIGIN: Record<string, MessageKey> = {
  BANK: "opex.origin.BANK",
  EXPENSE: "opex.origin.EXPENSE",
  MANUAL: "opex.origin.MANUAL",
  TEMPLATE: "opex.origin.TEMPLATE",
};

/**
 * Реестр «Витрати» (OPX-01): записи месяца, правила разнесения платежей
 * банка и шаблоны повторяющихся. Проводок нет: запись сразу в P&L по
 * статье. Одно семейство на запись (CST-20), без изделия ZAG (CST-22).
 */
export function OpexPage() {
  const { tab = "entries" } = route.useSearch();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const rules = useQuery(rulesQuery);
  const templates = useQuery(templatesQuery);
  const articles = useQuery(articlesQuery);
  const name = (code: string) => articles.data?.find((a) => a.code === code)?.name ?? code;

  return (
    <div className="page money">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          <AppIcon name="expense" size={32} />
          {t("opex.title")}
        </h1>
        <Button asChild variant="ghost">
          <Link to="/money/bank" search={{ tab: "accounts" }}>
            {t("bank.title")}
          </Link>
        </Button>
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice">{notice.body}</Notice> : null}
      <Tabs<OpexTab>
        value={tab}
        onValueChange={(v) => {
          setNotice(null);
          void navigate({ to: "/money/opex", search: { tab: v }, replace: true });
        }}
        label={t("opex.tabs")}
        items={[
          { value: "entries", label: t("opex.tab.entries") },
          { value: "rules", label: t("opex.tab.rules"), count: rules.data?.length },
          { value: "templates", label: t("opex.tab.templates"), count: templates.data?.filter((x) => x.active).length },
        ]}
      >
        <TabPanel value="entries" className="queue">
          <Entries articles={articles.data ?? []} name={name} onNotice={setNotice} />
        </TabPanel>
        <TabPanel value="rules" className="queue">
          <Rules rules={rules.data ?? []} articles={articles.data ?? []} name={name} onNotice={setNotice} />
        </TabPanel>
        <TabPanel value="templates" className="queue">
          <Templates templates={templates.data ?? []} articles={articles.data ?? []} name={name} onNotice={setNotice} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

const articleOptions = (articles: Article[]) => [{ value: "", label: t("opex.chooseArticle") }, ...articles.map((a) => ({ value: a.code, label: a.name }))];

/** Сумма из поля: запятая или точка, пробел тысяч. */
const amountOf = (raw: string): number | null => parseQty(raw);

interface Props {
  articles: Article[];
  name: (code: string) => string;
  onNotice: (n: NoticeState | null) => void;
}

function useSaved(onNotice: Props["onNotice"]) {
  const queryClient = useQueryClient();
  return (title: string) => ({
    onSuccess: () => {
      onNotice({ tone: "success", title });
      void queryClient.invalidateQueries({ queryKey: MONEY });
    },
    onError: (e: unknown) => onNotice(errorNotice(e)),
  });
}

function Entries({ articles, name, onNotice }: Props) {
  const [month, setMonth] = useState(today().slice(0, 7));
  const list = useQuery(entriesQuery(month));
  const saved = useSaved(onNotice);
  const add = useMutation({ mutationFn: moneyApi.addEntry });
  const [form, setForm] = useState({ date: today(), counterparty: "", article: "", amount: "", family: "", activity: "", description: "" });
  const total = (list.data ?? []).reduce((a, e) => a + e.amount, 0);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    const amount = amountOf(form.amount);
    if (amount === null) {
      onNotice({ tone: "danger", title: t("opex.amountNotNumber") });
      return;
    }
    const family = form.family.trim().toUpperCase();
    const done = saved(t("opex.entryAdded"));
    add.mutate(
      {
        date: form.date,
        counterparty: form.counterparty,
        article: form.article,
        amount,
        families: family ? { [family]: 1 } : {},
        activity: form.activity || null,
        description: form.description,
      },
      {
        onSuccess: () => {
          done.onSuccess();
          setForm({ ...form, counterparty: "", amount: "", description: "" });
          setMonth(form.date.slice(0, 7));
        },
        onError: done.onError,
      },
    );
  };

  return (
    <>
      <div className="money-month">
        <TextField label={t("opex.month")} type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <span className="w-num money-total">{t("opex.total", { sum: formatMoney(total, "UAH") })}</span>
      </div>
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} /> : null}
      <ListTable<Entry>
        label={t("opex.tab.entries")}
        rows={list.data ?? []}
        rowKey={(r) => String(r.id)}
        empty={<p className="w-muted">{t("opex.entries.empty")}</p>}
        columns={[
          { key: "who", header: t("opex.counterparty"), kind: "title", render: (r) => r.counterparty },
          { key: "article", header: t("opex.article"), kind: "text", render: (r) => name(r.article) },
          { key: "analytics", header: t("opex.analytics"), kind: "text", width: "7rem", render: (r) => [r.family, r.activity].filter(Boolean).join(" · ") },
          { key: "origin", header: t("opex.origin"), kind: "text", width: "8rem", render: (r) => <Badge>{t(ORIGIN[r.origin] ?? "opex.origin.MANUAL")}</Badge> },
          { key: "date", header: t("bank.date"), kind: "text", width: "5rem", render: (r) => formatDate(r.date) },
          { key: "amount", header: t("bank.amount"), kind: "number", width: "9rem", render: (r) => formatMoney(r.amount, "UAH") },
        ]}
      />
      <Plate cut="sm" bleed>
        <form className="money-form" onSubmit={submit}>
          <h2 className="money-form-title">{t("opex.newEntry")}</h2>
          <TextField label={t("bank.date")} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
          <TextField label={t("opex.counterparty")} value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} required />
          <SelectField label={t("opex.article")} value={form.article} onChange={(e) => setForm({ ...form, article: e.target.value })} required options={articleOptions(articles)} />
          <TextField label={t("opex.amount")} inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          <TextField label={t("opex.family")} hint={t("opex.familyHint")} value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} autoComplete="off" />
          <SelectField label={t("opex.activity")} value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} options={activityOptions()} />
          <TextField label={t("opex.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button type="submit" variant="create" disabled={add.isPending}>
            {t("opex.addEntry")}
          </Button>
        </form>
      </Plate>
    </>
  );
}

function Rules({ rules, articles, name, onNotice }: Props & { rules: Rule[] }) {
  const saved = useSaved(onNotice);
  const add = useMutation({ mutationFn: moneyApi.addRule });
  const remove = useMutation({ mutationFn: moneyApi.deleteRule });
  const [form, setForm] = useState({ priority: "100", edrpou: "", purposeContains: "", article: "", family: "", activity: "" });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    add.mutate(
      {
        priority: Number(form.priority) || 100,
        edrpou: form.edrpou.trim() || null,
        purposeContains: form.purposeContains.trim() || null,
        article: form.article,
        family: form.family.trim() || null,
        activity: form.activity || null,
      },
      saved(t("opex.ruleAdded")),
    );
  };

  return (
    <>
      <p className="w-muted">{t("opex.rules.lead")}</p>
      {rules.length === 0 ? <p className="w-muted">{t("opex.rules.empty")}</p> : null}
      <ul className="money-rows">
        {rules.map((r) => (
          <li key={r.id} className="money-row">
            <span className="money-row-main">
              <span>{[r.edrpou ? t("bank.edrpou", { code: r.edrpou }) : null, r.purposeContains ? t("opex.purposeHas", { word: r.purposeContains }) : null].filter(Boolean).join(" + ")}</span>
              <span className="w-muted">{`→ ${name(r.article)} · ${[r.family, r.activity].filter(Boolean).join(" · ")} · ${t("opex.priority", { n: r.priority })}`}</span>
            </span>
            <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(r.id, saved(t("opex.ruleDeleted")))}>
              {t("opex.deleteRule")}
            </Button>
          </li>
        ))}
      </ul>
      <Plate cut="sm" bleed>
        <form className="money-form" onSubmit={submit}>
          <h2 className="money-form-title">{t("opex.newRule")}</h2>
          <TextField label={t("opex.ruleEdrpou")} inputMode="numeric" value={form.edrpou} onChange={(e) => setForm({ ...form, edrpou: e.target.value })} autoComplete="off" />
          <TextField label={t("opex.rulePurpose")} value={form.purposeContains} onChange={(e) => setForm({ ...form, purposeContains: e.target.value })} />
          <SelectField label={t("opex.article")} value={form.article} onChange={(e) => setForm({ ...form, article: e.target.value })} required options={articleOptions(articles)} />
          <TextField label={t("opex.family")} hint={t("opex.familyHint")} value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} autoComplete="off" />
          <SelectField label={t("opex.activity")} value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} options={activityOptions()} />
          <TextField label={t("opex.priorityField")} inputMode="numeric" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          <Button type="submit" variant="create" disabled={add.isPending}>
            {t("opex.addRule")}
          </Button>
        </form>
      </Plate>
    </>
  );
}

function Templates({ templates, articles, name, onNotice }: Props & { templates: Template[] }) {
  const saved = useSaved(onNotice);
  const add = useMutation({ mutationFn: moneyApi.addTemplate });
  const stop = useMutation({ mutationFn: moneyApi.stopTemplate });
  const [form, setForm] = useState({ name: "", article: "", amount: "", counterparty: "", dayOfMonth: "1", fromMonth: today().slice(0, 7), family: "", activity: "" });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    const amount = amountOf(form.amount);
    if (amount === null) {
      onNotice({ tone: "danger", title: t("opex.amountNotNumber") });
      return;
    }
    add.mutate(
      {
        name: form.name,
        article: form.article,
        amount,
        counterparty: form.counterparty,
        dayOfMonth: Number(form.dayOfMonth),
        fromMonth: form.fromMonth,
        family: form.family.trim() || null,
        activity: form.activity || null,
      },
      saved(t("opex.templateAdded")),
    );
  };

  return (
    <>
      <p className="w-muted">{t("opex.templates.lead")}</p>
      {templates.length === 0 ? <p className="w-muted">{t("opex.templates.empty")}</p> : null}
      <ul className="money-rows">
        {templates.map((x) => (
          <li key={x.id} className="money-row" data-stopped={x.active ? undefined : ""}>
            <span className="money-row-main">
              <span>{x.name}</span>
              <span className="w-muted">{`${name(x.article)} · ${x.counterparty} · ${t("opex.everyMonth", { day: x.dayOfMonth })}`}</span>
            </span>
            <span className="w-num">{formatMoney(x.amount, "UAH")}</span>
            {x.active ? (
              <Button variant="ghost" disabled={stop.isPending} onClick={() => stop.mutate(x.id, saved(t("opex.templateStopped")))}>
                {t("opex.stopTemplate")}
              </Button>
            ) : (
              <Badge>{t("opex.stopped")}</Badge>
            )}
          </li>
        ))}
      </ul>
      <Plate cut="sm" bleed>
        <form className="money-form" onSubmit={submit}>
          <h2 className="money-form-title">{t("opex.newTemplate")}</h2>
          <TextField label={t("opex.templateName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <SelectField label={t("opex.article")} value={form.article} onChange={(e) => setForm({ ...form, article: e.target.value })} required options={articleOptions(articles)} />
          <TextField label={t("opex.amount")} inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          <TextField label={t("opex.counterparty")} value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} required />
          <TextField label={t("opex.dayOfMonth")} type="number" min={1} max={31} value={form.dayOfMonth} onChange={(e) => setForm({ ...form, dayOfMonth: e.target.value })} required />
          <TextField label={t("opex.fromMonth")} type="month" value={form.fromMonth} onChange={(e) => setForm({ ...form, fromMonth: e.target.value })} required />
          <TextField label={t("opex.family")} hint={t("opex.familyHint")} value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} autoComplete="off" />
          <SelectField label={t("opex.activity")} value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} options={activityOptions()} />
          <Button type="submit" variant="create" disabled={add.isPending}>
            {t("opex.addTemplate")}
          </Button>
        </form>
      </Plate>
    </>
  );
}
